import React, { memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { createActions } from '../../../store';
import { HexToRgb } from '../../../../../../utilities/colorUtilities';
import ColorPickerListItem from '../../../../../list-items/ColorPickerListItem';

const TOOLTIPS = {
    ADD: 'Click the "+" button to add a secondary color. The main color will change into the secondary color during loud noises.',
    REMOVE: 'Click the "🡪" button to remove the secondary color. The main color will change into the secondary color during loud noises.',
    BASIC: 'The main color will change into the secondary color during loud noises.'
};

const WaveLayerLineColorListItem = memo(({
    waveLayerLineColor,
    waveLayerLineAlpha,
    waveLayerSecondaryLineColor,
    waveLayerSecondaryLineAlpha,
    index,
    updateWaveLayer,
    advanced
}) => {
    const onColorChange = useCallback((color) => {
        updateWaveLayer(index, {
            lineColor: color.hex === 'transparent' ? '#000000' : color.hex.replace('#', '0x'),
            lineAlpha: color.rgb.a
        });
    }, [updateWaveLayer, index]);

    const onSecondaryColorChange = useCallback((color) => {
        if (color) {
            updateWaveLayer(index, {
                secondaryLineColor: color.hex === 'transparent' ? '#000000' : color.hex.replace('#', '0x'),
                secondaryLineAlpha: color.rgb.a
            });
        } else {
            updateWaveLayer(index, {
                secondaryLineColor: null
            });
        }
    }, [updateWaveLayer, index]);

    const colors = useMemo(() => {
        const lineColor = HexToRgb(waveLayerLineColor);
        lineColor.a = waveLayerLineAlpha;

        let secondaryLineColor = null;
        if (waveLayerSecondaryLineColor) {
            secondaryLineColor = HexToRgb(waveLayerSecondaryLineColor);
            secondaryLineColor.a = waveLayerSecondaryLineAlpha;
        }

        return { lineColor, secondaryLineColor };
    }, [waveLayerLineColor, waveLayerLineAlpha, waveLayerSecondaryLineColor, waveLayerSecondaryLineAlpha]);

    const tooltip = useMemo(() => {
        if (!advanced && !colors.secondaryLineColor) return null;
        if (advanced) {
            return colors.secondaryLineColor ? TOOLTIPS.REMOVE : TOOLTIPS.ADD;
        }
        return TOOLTIPS.BASIC;
    }, [advanced, colors.secondaryLineColor]);

    return (
        <ColorPickerListItem
            text='Outline Color'
            color={colors.lineColor}
            secondaryColor={colors.secondaryLineColor}
            onChange={onColorChange}
            onSecondaryChange={onSecondaryColorChange}
            allowSecondary={advanced || colors.secondaryLineColor}
            toolTip={tooltip}
            disableSecondaryButton={!advanced}
        />
    );
});

const mapStateToProps = (state, { index }) => {
    const {
        lineColor: waveLayerLineColor,
        lineAlpha: waveLayerLineAlpha,
        secondaryLineColor: waveLayerSecondaryLineColor,
        secondaryLineAlpha: waveLayerSecondaryLineAlpha
    } = state.createPreview.present.videoSettings.visualizer.waveCircles[index];

    return {
        waveLayerLineColor,
        waveLayerLineAlpha,
        waveLayerSecondaryLineColor,
        waveLayerSecondaryLineAlpha
    };
};

const mapDispatchToProps = {
    updateWaveLayer: (index, properties) => createActions.updateWaveLayer(index, properties)
};

export default connect(mapStateToProps, mapDispatchToProps)(WaveLayerLineColorListItem);